import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription } from '@/components/ui/card';
import { Copy, UserPlus, Clock, CheckCircle, Shield } from 'lucide-react';
import { useBrandOrganization } from '@/hooks/useBrandOrganization';

interface BrandInviteModalProps {
  children: React.ReactNode;
}

export const BrandInviteModal: React.FC<BrandInviteModalProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inviteCode, setInviteCode] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [role, setRole] = useState<string>('member');
  
  const [permissions, setPermissions] = useState({
    can_view_deals: true,
    can_create_deals: false,
    can_edit_deals: false,
    can_delete_deals: false,
    can_view_payments: true,
    can_make_payments: false,
    can_view_analytics: true,
    can_manage_campaigns: false,
    can_manage_members: false,
    can_view_contacts: true,
    can_export_data: false,
  });

  const { generateInvite } = useBrandOrganization();

  const allPermissionsEnabled = Object.values(permissions).every(v => v);

  const toggleAllPermissions = () => {
    const newValue = !allPermissionsEnabled;
    setPermissions({
      can_view_deals: newValue,
      can_create_deals: newValue,
      can_edit_deals: newValue,
      can_delete_deals: newValue,
      can_view_payments: newValue,
      can_make_payments: newValue,
      can_view_analytics: newValue,
      can_manage_campaigns: newValue,
      can_manage_members: newValue,
      can_view_contacts: newValue,
      can_export_data: newValue,
    });
  };

  const permissionsList = [
    { key: 'can_view_deals', label: 'View Deals', description: 'Can see all deals posted by the organization' },
    { key: 'can_create_deals', label: 'Create Deals', description: 'Can create new deals on behalf of the organization' },
    { key: 'can_edit_deals', label: 'Edit Deals', description: 'Can modify existing deals' },
    { key: 'can_delete_deals', label: 'Delete Deals', description: 'Can remove deals' },
    { key: 'can_view_payments', label: 'View Payments', description: 'Can see payment history and status' },
    { key: 'can_make_payments', label: 'Make Payments', description: 'Can authorize and make payments for deals' },
    { key: 'can_view_analytics', label: 'View Analytics', description: 'Can access performance reports and analytics' },
    { key: 'can_manage_campaigns', label: 'Manage Campaigns', description: 'Can create and manage marketing campaigns' },
    { key: 'can_manage_members', label: 'Manage Members', description: 'Can invite and manage other team members' },
    { key: 'can_view_contacts', label: 'View Contacts', description: 'Can see contact information for talents and partners' },
    { key: 'can_export_data', label: 'Export Data', description: 'Can export reports and data' },
  ];

  const handleGenerateInvite = async () => {
    setLoading(true);
    try {
      const data = await generateInvite(role, permissions);
      if (data) {
        setInviteCode(data.code);
        setExpiresAt(new Date(data.expires_at).toLocaleString());
      }
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    if (inviteCode) {
      await navigator.clipboard.writeText(inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setInviteCode('');
      setExpiresAt('');
      setCopied(false);
    }
  };

  const togglePermission = (key: string) => {
    setPermissions(prev => ({ ...prev, [key]: !prev[key as keyof typeof prev] }));
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Invite Brand Team Member
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {!inviteCode ? (
            <>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="role">Member Role</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Permissions
                    </Label>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="toggle-all" className="text-sm text-muted-foreground cursor-pointer">
                        {allPermissionsEnabled ? 'Disable All' : 'Enable All'}
                      </Label>
                      <Switch
                        id="toggle-all"
                        checked={allPermissionsEnabled}
                        onCheckedChange={toggleAllPermissions}
                      />
                    </div>
                  </div>
                  <Card>
                    <CardContent className="pt-6 space-y-4">
                      {permissionsList.map((perm) => (
                        <div key={perm.key} className="flex items-start justify-between space-x-4">
                          <div className="flex-1">
                            <Label htmlFor={perm.key} className="font-medium cursor-pointer">
                              {perm.label}
                            </Label>
                            <CardDescription className="text-xs mt-0.5">
                              {perm.description}
                            </CardDescription>
                          </div>
                          <Switch
                            id={perm.key}
                            checked={permissions[perm.key as keyof typeof permissions]}
                            onCheckedChange={() => togglePermission(perm.key)}
                          />
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>
              </div>
              
              <Button 
                onClick={handleGenerateInvite} 
                disabled={loading}
                className="w-full"
              >
                {loading ? "Generating..." : "Generate Invite Code"}
              </Button>
            </>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Invite Code</Label>
                <div className="flex gap-2">
                  <Input
                    id="code"
                    value={inviteCode}
                    readOnly
                    className="font-mono text-lg tracking-wider text-center"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={copyCode}
                    className="shrink-0"
                  >
                    {copied ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>Expires: {expiresAt}</span>
              </div>
              
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <p className="text-sm font-medium">Instructions:</p>
                <p className="text-sm text-muted-foreground">
                  Share this code with your team member. They can enter it in their brand account settings to join your organization with the configured permissions.
                </p>
              </div>
              
              <Button 
                onClick={handleGenerateInvite} 
                disabled={loading}
                variant="outline"
                className="w-full"
              >
                Generate New Code
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};